import type { Currency } from '@ps/web-core-enums';
import classNames from 'clsx';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Translation from '@swf/components/Translation/Translation';
import type { ResultType, WalletInfo } from '@swf/generated/types/api/sports';

import {
  TXTACL_StakeX,
  TXTWCL_Cashed_OutX,
  TXTWCL_N0_Bonus_Cash,
  TXTWCL_N0_Free_Bet,
  TXTWCL_Potential_ReturnsX,
  TXTWCL_ReturnedX,
  TXTWCL_n_a
} from './StakeAndReturn.messages.json';
import styles from './StakeAndReturn.scss';

interface Props {
  result?: ResultType;
  potentialWin?: string;
  actualWin?: number;
  totalStake: number;
  isSettled?: boolean;
  cashedOutAmount?: number;
  currency?: Currency;
  walletInfo: WalletInfo;
  isProfitableBet?: boolean;
  isStartingPriceBet: boolean;
}

const StakeAndReturn: React.FC<Props> = ({
  isSettled,
  totalStake,
  result,
  actualWin,
  potentialWin,
  cashedOutAmount,
  currency,
  walletInfo: { hasBonusCash, hasFreeBets },
  isProfitableBet,
  isStartingPriceBet
}) => {
  let returnValue: number;

  if (isSettled && actualWin) {
    returnValue = actualWin;
  } else if (cashedOutAmount) {
    returnValue = cashedOutAmount;
  } else if (isSettled && isStartingPriceBet) {
    returnValue = 0;
  } else {
    returnValue = Number(potentialWin);
  }

  const hasWon = result === 'WON' || result === 'PLACED' || result === 'CASHED_OUT';
  const hasHighlightedReturnValue =
    result !== 'VOID' && ((isProfitableBet && (isSettled || hasWon)) || cashedOutAmount);
  const isExtraWalletBet = hasBonusCash || hasFreeBets;
  const isVoidedExtraWalletBet = isExtraWalletBet && result === 'VOID';

  return (
    <div className={styles.wrapper}>
      <div>
        <span className={styles.title}>
          <Translation {...TXTACL_StakeX} />
        </span>
        <span
          className={classNames(styles.amount, {
            [styles.extraWallet]: isExtraWalletBet
          })}
        >
          {isExtraWalletBet ? (
            <Translation
              {...(hasBonusCash ? TXTWCL_N0_Bonus_Cash : TXTWCL_N0_Free_Bet)}
              values={{ 0: <CurrencyAmount dollarValue value={totalStake} currency={currency} /> }}
            />
          ) : (
            <CurrencyAmount dollarValue value={totalStake} currency={currency} />
          )}
        </span>
      </div>
      <div>
        <span className={styles.title}>
          {result === 'CASHED_OUT' || cashedOutAmount ? (
            <Translation {...TXTWCL_Cashed_OutX} />
          ) : (
            <Translation {...(isSettled ? TXTWCL_ReturnedX : TXTWCL_Potential_ReturnsX)} />
          )}
        </span>
        <span
          data-testid="bet-return-value"
          className={classNames(styles.amount, {
            [styles.won]: hasHighlightedReturnValue,
            [styles.extraWallet]: isVoidedExtraWalletBet
          })}
        >
          {isStartingPriceBet && !isSettled ? (
            <Translation {...TXTWCL_n_a} />
          ) : isVoidedExtraWalletBet ? (
            <Translation
              {...(hasBonusCash ? TXTWCL_N0_Bonus_Cash : TXTWCL_N0_Free_Bet)}
              values={{ 0: <CurrencyAmount dollarValue value={returnValue} currency={currency} /> }}
            />
          ) : (
            <CurrencyAmount dollarValue value={returnValue} currency={currency} />
          )}
        </span>
      </div>
    </div>
  );
};

export default StakeAndReturn;
